import { createHttpService } from '../ApiService';
import { mockClassGradeSubjectStructure } from 'src/__mocks__/institute/classGradeSubjectStructureMock';
import { mockGradeAndFeeStructure } from '../../__mocks__/students/studentAdmissionFeeMock';
import { getFeeStructureMock } from '../../__mocks__/feeStructureMock';
import {
  IClassAndFeeStructureSchemaResponse,
  IFeeStructureParams,
  IFeeStructureResponse,
  IInstituteLeavesResponse,
  IAdminGetFeeComponentResponse,
  IAdminGetGradeFeeStructureResponse,
  IAdminGetPricingRuleResponse,
  IAdminGetFeeScheduleResponse,
} from './institute.interfaces';
import { mockInstituteLeaves } from '../../__mocks__/institute/instituteLeavesMock';
import {
  ADMIN_FEE_BASE,
  mapClassCategoryToAdmissionFeeCategory,
  normalizeArrayResponse,
} from './adminFeeStructure.helpers';
import isAppWithMock from 'src/utils/isAppWithMock';

export const useInstituteService = () => {
  const httpAPI = createHttpService();
  const withMockData = isAppWithMock();

  /** GET /v1/admin/feeStructure/class-fees — canonical class → category → fee rows */
  const fetchClassFees = (query?: { classId?: string; academicYear?: string }) =>
    httpAPI
      .get<IClassAndFeeStructureSchemaResponse>(
        `${ADMIN_FEE_BASE}/class-fees`,
        {
          params: {
            ...(query?.classId?.trim()
              ? { classId: query.classId.trim() }
              : {}),
            ...(query?.academicYear?.trim()
              ? { academicYear: query.academicYear.trim() }
              : {}),
          },
        }
      )
      .then((body) => ({
        classes: Array.isArray(body?.classes) ? body.classes : [],
      }));

  const fetchAdminFeeComponents = () =>
    httpAPI
      .get<IAdminGetFeeComponentResponse[]>(`${ADMIN_FEE_BASE}/components`)
      .then((rows) =>
        normalizeArrayResponse<IAdminGetFeeComponentResponse>(rows)
      );

  const getGradeAndFeeStructure =
    async (): Promise<IClassAndFeeStructureSchemaResponse> => {
      if (withMockData) {
        return mockGradeAndFeeStructure;
      }
      return fetchClassFees();
    };

  const getClassGradeSubjectStructure = () => {
    return Promise.resolve(mockClassGradeSubjectStructure);
  };

  const getFeeStructure = async (
    params: IFeeStructureParams
  ): Promise<IFeeStructureResponse> => {
    if (withMockData) {
      return getFeeStructureMock(
        params.grade,
        params.section,
        params.category
      );
    }
    void params.section;
    const classId = params.grade?.trim() ?? '';
    const categoryId = params.category?.trim() ?? '';
    if (!classId || !categoryId) {
      return [];
    }
    const { classes } = await fetchClassFees({ classId });
    const cls = classes[0];
    if (!cls) {
      return [];
    }
    const category = cls.categories.find((c) => c.categoryId === categoryId);
    if (!category) {
      return [];
    }
    return [mapClassCategoryToAdmissionFeeCategory(category)];
  };

  const getInstituteLeaves = (): Promise<IInstituteLeavesResponse> => {
    if (withMockData) {
      return Promise.resolve(mockInstituteLeaves);
    }
    return httpAPI
      .get<IInstituteLeavesResponse>('/institute/leaves')
      .then((res: any) => res?.data ?? res ?? { leaveTypes: [] });
  };

  /** @deprecated naming — returns reusable fee components (`GET …/components`) */
  const getAdminFeeCategories = () => fetchAdminFeeComponents();

  const getAdminGradeFeeStructures = (): Promise<
    IAdminGetGradeFeeStructureResponse[]
  > => {
    if (withMockData) {
      return Promise.resolve(
        mockGradeAndFeeStructure.classes.map((c) => ({
          id: c.classId,
          instituteId: '',
          gradeId: c.classId,
          gradeName: c.className,
          gradeDescription: c.classDescription,
          categories: c.categories.map((cat) => cat.categoryId),
          createdAt: '',
          updatedAt: '',
        }))
      );
    }
    return fetchClassFees().then(({ classes }) =>
      classes.map((c) => ({
        id: c.classId,
        instituteId: '',
        gradeId: c.classId,
        gradeName: c.className,
        gradeDescription: c.classDescription,
        categories: c.categories.map((cat) => cat.categoryId),
        createdAt: '',
        updatedAt: '',
      }))
    );
  };

  /** `feeCategoryId` is a fee **component** id (`FeeComponentTypes._id`) */
  const getAdminFeePricingRulesForCategory = (feeCategoryId: string) => {
    if (!feeCategoryId.trim()) {
      return Promise.resolve<IAdminGetPricingRuleResponse[]>([]);
    }
    if (withMockData) {
      return Promise.resolve([]);
    }
    return httpAPI
      .get<IAdminGetPricingRuleResponse[]>(
        `${ADMIN_FEE_BASE}/components/${encodeURIComponent(
          feeCategoryId.trim()
        )}/pricing-rules`
      )
      .then((rows) => normalizeArrayResponse(rows));
  };

  const getAdminFeeSchedulesForCategory = (
    feeCategoryId: string
  ): Promise<IAdminGetFeeScheduleResponse[]> => {
    if (!feeCategoryId.trim()) {
      return Promise.resolve([]);
    }
    if (withMockData) {
      return Promise.resolve([]);
    }
    return httpAPI
      .get<IAdminGetFeeScheduleResponse[]>(
        `${ADMIN_FEE_BASE}/components/${encodeURIComponent(
          feeCategoryId.trim()
        )}/schedules`
      )
      .then((rows) => normalizeArrayResponse(rows));
  };

  return {
    getGradeAndFeeStructure,
    getClassGradeSubjectStructure,
    getFeeStructure,
    getInstituteLeaves,
    getAdminFeeCategories,
    getAdminGradeFeeStructures,
    getAdminFeePricingRulesForCategory,
    getAdminFeeSchedulesForCategory,
  };
};
